import React, { Suspense } from 'react'
import 'react-international-phone/style.css'
import { shallowEqual, useSelector } from 'react-redux'
import { Route, Routes } from 'react-router-dom'

import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { setViewportFullHeight } from 'helpers'
import { PageInfoProvider } from 'providers/PageInfoProvider'
import { polyfill as smoothScrollPolyfill } from 'smoothscroll-polyfill'
import 'swiper/css'
import 'swiper/css/bundle'

import { selectConfig, selectCurrentVariant } from 'root-redux/selects/common'
import { selectUserStatus } from 'root-redux/selects/user'

import { useFacebookPixelInitialization } from 'hooks/analytics/useFacebookPixelInitialization'
import { useGoogleAnalytics } from 'hooks/analytics/useGoogleAnalytics'
import { useHotJarInitialization } from 'hooks/analytics/useInitHotJar'
import { useSendABSegment } from 'hooks/analytics/useSendABSegment'
import { useSnapPixelInitialization } from 'hooks/analytics/useSnapPixelInitialization'
import { useTiktokPixelInitialization } from 'hooks/analytics/useTiktokPixelInitialization'
import { useEbanxInit } from 'hooks/common/useEbanxInit'
import { useInitAppConfig } from 'hooks/common/useInitAppConfig'
import { useLanguage } from 'hooks/common/useLanguage'
import { useSetVariant } from 'hooks/common/useSetVariant'
import { growthbook, useGrowthBook } from 'hooks/services/useGrowthBook'
import { useInContextInitialization } from 'hooks/services/useInContextInitialization'
import { useScrollFix } from 'hooks/ui/useScrollFix'
import { useSetUserStatus } from 'hooks/user/useSetUserStatus'
import { useSetUuid } from 'hooks/user/useSetUuid'

import { CookiePolicy } from 'pages/cookie-policy'
import { PrivacyPolicy } from 'pages/privacy-policy'
import { TermsOfUse } from 'pages/terms-of-use'

import { LayoutProvider } from 'components/LayoutProvider'
import { MetaInfo } from 'components/MetaInfo'
import { Spinner } from 'components/Spinner'

import { browserHistory } from 'browser-history'
import { TOU_PP_COOKIE_LINKS } from 'root-constants/common'

import { CustomRouter } from './CustomRouter'

setViewportFullHeight()
smoothScrollPolyfill()

export const App: React.FC = () => {
  const userStatus = useSelector(selectUserStatus, shallowEqual)
  const variant = useSelector(selectCurrentVariant, shallowEqual)
  const config = useSelector(selectConfig, shallowEqual)

  useInitAppConfig()
  useGoogleAnalytics()
  useFacebookPixelInitialization()
  useTiktokPixelInitialization()
  useSnapPixelInitialization()
  useInContextInitialization()
  useSetVariant()
  useLanguage()
  useSetUuid()
  useSetUserStatus()
  useScrollFix()
  useGrowthBook()
  useHotJarInitialization()
  useEbanxInit()
  useSendABSegment()

  return (
    <CustomRouter history={browserHistory}>
      <Suspense fallback={<Spinner />}>
        <MetaInfo />

        {TOU_PP_COOKIE_LINKS.includes(window.location.pathname) ? (
          <Routes>
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />
          </Routes>
        ) : (
          <GrowthBookProvider growthbook={growthbook}>
            {userStatus && variant && config ? (
              <PageInfoProvider>
                <LayoutProvider />
              </PageInfoProvider>
            ) : (
              <Spinner />
            )}
          </GrowthBookProvider>
        )}
      </Suspense>
    </CustomRouter>
  )
}
